import React, { useState } from 'react';
import { Building2, Plus, Info, ArrowRight } from 'lucide-react';
import { Button, Card } from '../design-system';
import CreatePracticeForm from './CreatePracticeForm';
import JoinPracticeForm from './JoinPracticeForm';

const PracticeSelection = ({ onPracticeSelected }) => {
  const [selectedOption, setSelectedOption] = useState(null);

  const handleSkip = () => {
    // Allow therapist to continue without a practice
    if (onPracticeSelected) {
      onPracticeSelected();
    }
  };

  if (selectedOption === 'create') {
    return <CreatePracticeForm onSuccess={onPracticeSelected} onCancel={() => setSelectedOption(null)} />;
  }

  if (selectedOption === 'join') {
    return <JoinPracticeForm onSuccess={onPracticeSelected} onCancel={() => setSelectedOption(null)} />;
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: 'var(--color-background)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      padding: 'var(--space-8) var(--space-4)'
    }}>
      <div style={{ maxWidth: '600px', width: '100%', display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
        <div>
          <h2 style={{ 
            marginTop: 'var(--space-6)', 
            textAlign: 'center', 
            fontSize: 'var(--text-h1)', 
            fontWeight: 'var(--weight-bold)', 
            color: 'var(--color-text)',
            fontFamily: 'var(--font-heading)',
            marginBottom: 'var(--space-2)'
          }}>
            Join or Create a Practice
          </h2>
          <p style={{ 
            textAlign: 'center', 
            fontSize: 'var(--text-body)', 
            color: 'var(--color-text)', 
            opacity: 0.8,
            margin: 0
          }}>
            Choose how you'd like to get started
          </p>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-4)', '@media (min-width: 768px)': { gridTemplateColumns: '1fr 1fr' } }}>
          <Card 
            variant="default" 
            padding="large"
            style={{ 
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              border: '2px solid transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onClick={() => setSelectedOption('join')}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-4)' }}>
              <div style={{ 
                width: '64px', 
                height: '64px', 
                borderRadius: '50%', 
                backgroundColor: 'var(--color-primary-light)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Building2 size={32} color="var(--color-primary)" />
              </div>
              <h3 style={{ 
                fontSize: 'var(--text-h3)', 
                fontWeight: 'var(--weight-semibold)', 
                color: 'var(--color-text)',
                margin: 0,
                textAlign: 'center'
              }}>
                Join Existing Practice
              </h3>
              <p style={{ 
                fontSize: 'var(--text-caption)', 
                color: 'var(--color-text)', 
                opacity: 0.7,
                margin: 0,
                textAlign: 'center'
              }}>
                I have a Practice ID from my practice admin
              </p>
            </div>
          </Card>

          <Card 
            variant="default" 
            padding="large"
            style={{ 
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              border: '2px solid transparent',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onClick={() => setSelectedOption('create')}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-4)' }}>
              <div style={{ 
                width: '64px', 
                height: '64px', 
                borderRadius: '50%', 
                backgroundColor: 'var(--color-success-light)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Plus size={32} color="var(--color-success)" />
              </div>
              <h3 style={{ 
                fontSize: 'var(--text-h3)', 
                fontWeight: 'var(--weight-semibold)', 
                color: 'var(--color-text)',
                margin: 0,
                textAlign: 'center'
              }}>
                Create New Practice
              </h3>
              <p style={{ 
                fontSize: 'var(--text-caption)', 
                color: 'var(--color-text)', 
                opacity: 0.7,
                margin: 0,
                textAlign: 'center'
              }}>
                Start your own practice. Perfect for solo practitioners too!
              </p>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 'var(--space-1)',
                marginTop: 'var(--space-2)',
                fontSize: 'var(--text-caption)',
                color: 'var(--color-text)',
                opacity: 0.6
              }}>
                <Info size={14} />
                <span>You can always invite others later if you want to grow.</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Skip Option */}
        <div style={{ textAlign: 'center', marginTop: 'var(--space-4)' }}>
          <Button
            variant="ghost"
            size="medium"
            onClick={handleSkip}
            style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: 'var(--space-2)',
              color: 'var(--color-text)',
              opacity: 0.7
            }}
          >
            Continue without a practice
            <ArrowRight size={16} />
          </Button>
          <p style={{ 
            fontSize: 'var(--text-caption)', 
            color: 'var(--color-text)', 
            opacity: 0.6,
            marginTop: 'var(--space-2)',
            margin: 0
          }}>
            You can create or join a practice later from your dashboard
          </p>
        </div>
      </div>
    </div>
  );
};

export default PracticeSelection;

